import * as Accordion from '@radix-ui/react-accordion';
import { isEmpty } from 'lodash';

import { ContainerStack } from '@/components/container-stack';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { DISTANCE_UNITS, UNIT_CO2 } from '@/lib/constants';
import { EmissionsCalculationModel } from '@/lib/models/emissions/types';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { ShipmentCargo } from '@/lib/models/shipment/types';
import { VehicleDictionaryModel } from '@/lib/models/vehicle/types';
import { transformCo2EmissionToRadialData } from '@/lib/transformers/emissions';
import { getEmissionCalculationTotals, transformEmissionsModelToModeBreakdown } from '@/utils/emissions';
import { formatDistance } from '@/utils/format';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { formatEmissionsNum, formatEmissionsString } from '@/utils/format/emissions';
import { tv } from '@/utils/styles';

import { EmissionsSegmentBreakdown } from '../segment-breakdown';
import { EmissionsTable } from '../table';

export interface EmissionsReportProps {
  emissions: EmissionsCalculationModel[];
  vehicles: VehicleDictionaryModel;
  locations: LocationDictionaryModel;
  dateGenerated?: string | Date | null;
  cargo?: ShipmentCargo | ShipmentCargo[];
  isRealData?: boolean;
  className?: string;
}

const EmissionsReport = ({
  emissions,
  vehicles,
  locations,
  dateGenerated,
  cargo,
  isRealData = true,
  className,
}: EmissionsReportProps) => {
  if (isEmpty(emissions)) return null;

  const { base, highlight, dateStamp, statement, subHeading, sectionTitle, subText, divider } = styles();

  const {
    co2e: totalCo2e,
    intensity: totalIntensity,
    activity: totalActivity,
  } = getEmissionCalculationTotals(emissions);

  const emissionValueChartData = transformCo2EmissionToRadialData(totalCo2e);
  const emissionsModeBreakdown = transformEmissionsModelToModeBreakdown(emissions);

  return (
    <section className={base({ className })}>
      <header className="relative">
        <Heading className={sectionTitle()} variant="h4">
          Emissions summary
        </Heading>
        <div className="my-4">
          <div className="flex items-center gap-6">
            {/* Cargo component with fan-out effect on hover */}
            <ContainerStack cargo={cargo} />
            <div>
              <span className={subHeading()}>Emissions ({UNIT_CO2})</span>
              <Heading variant="h1" className="m-0 -mt-1 font-bold" as="h2">
                {formatEmissionsNum(totalCo2e.wtw)}
              </Heading>
            </div>
          </div>
        </div>
        <Heading as="h5" className={subHeading({ className: 'mb-1' })}>
          Statement
        </Heading>
        {!isRealData && (
          <Text className={statement()}>
            This is sample data. The emissions shown are not representative of actual emissions.
          </Text>
        )}
        <Text className={statement()}>
          This shipment is estimated to produce{' '}
          <span className={highlight()}>{formatEmissionsString(totalCo2e.wtw, totalCo2e.unit, false)}</span> from{' '}
          <span className={highlight()}>{formatDistance(totalActivity, DISTANCE_UNITS.TKM)}</span> of activity, with an
          emissions intensity ratio of{' '}
          <span className={highlight()}>
            {totalIntensity} {UNIT_CO2}/{DISTANCE_UNITS.TKM}
          </span>
        </Text>
        <div className={dateStamp()}>Updated: {formatDateTimeForUser(dateGenerated || '')}</div>
      </header>
      <span className={divider()} />
      <EmissionsTable
        title="Emissions lifecycle analysis"
        description="Breakdown of the full Wheel-to-Wake lifecycle"
        chartData={emissionValueChartData}
        totalCo2e={totalCo2e}
      />
      <span className={divider()} />
      <EmissionsTable
        title="Emissions by mode"
        description="Breakdown of emissions by mode of transport"
        chartData={emissionsModeBreakdown}
        totalCo2e={totalCo2e}
      />
      <span className={divider()} />
      <footer>
        <Heading className={sectionTitle()} variant="h4">
          Emissions by transport segment
        </Heading>
        <Text className={subText()}>
          {isRealData
            ? `This outlines the methodology and assumptions used to calculate each transport segment. Click each segment to
          see the details around how we calculated the emissions.`
            : 'This is sample data. The emissions shown are not representative of actual emissions.'}
        </Text>
        <div className="mt-4">
          <Accordion.Root type="multiple">
            {emissions.map((row) => {
              const key = `${row.fromId}${row.toId}`;
              return (
                <EmissionsSegmentBreakdown
                  key={key}
                  id={key}
                  data={row}
                  cargo={cargo && (Array.isArray(cargo) ? cargo : [cargo])}
                  vehicles={vehicles}
                  locations={locations}
                />
              );
            })}
          </Accordion.Root>
        </div>
      </footer>
    </section>
  );
};

const styles = tv({
  slots: {
    base: 'relative',
    divider: 'my-10 block border-b border-grey-200',
    statement: 'mb-0 max-w-[600px] text-baseLg',
    dateStamp: 'mt-2 text-xs italic text-text-secondary',
    highlight: 'whitespace-nowrap bg-green-100/50 px-1 py-0.5 font-medium text-green-800',
    sectionTitle: 'mb-1 text-base font-medium leading-snug',
    subHeading: 'mb-0 !text-baseSm font-normal leading-normal text-grey-600',
    subText: 'm-0 text-sm text-grey-600 [&_b]:font-semibold [&_b]:text-text-primary',
  },
});

export { EmissionsReport };
